/**
 * Stock History Page
 * View complete stock movement history with filters
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth, formatINR, formatIST } from '../lib/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { 
  History, Search, Filter, ArrowDown, ArrowUp, Package, 
  Truck, Undo2, Calendar, User, FileText, ChevronDown, ChevronUp, X
} from 'lucide-react';

const STOCK_CATEGORIES = ['Plywood', 'Blockboard', 'Doors', 'MDF', 'WPC/PVC'];

export default function StockHistoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, can } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  
  // Filters - initialise from URL params if present
  const [filterCategory, setFilterCategory] = useState(searchParams.get('category') || 'all');
  const [filterType, setFilterType] = useState('all');
  const [filterDays, setFilterDays] = useState('30');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('brand') || '');
  const [filterThickness, setFilterThickness] = useState(searchParams.get('thickness') || '');
  const [filterSize, setFilterSize] = useState(searchParams.get('size') || '');
  
  // Expanded items for details
  const [expandedItems, setExpandedItems] = useState({});

  // Check access
  const canAccess = can('stock.manage');

  useEffect(() => {
    if (user && !canAccess) {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, canAccess, navigate]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        days: parseInt(filterDays) || 30,
        limit: 200
      };
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterType !== 'all') params.change_type = filterType;
      if (searchQuery) params.brand_name = searchQuery;
      if (filterThickness) params.thickness = filterThickness;
      if (filterSize) params.size = filterSize;

      const res = await api.getStockHistory(params);
      setHistory(res.data.history || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterType, filterDays, searchQuery, filterThickness, filterSize]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.getStockHistorySummary(parseInt(filterDays) || 30);
      setSummary(res.data.summary || {});
    } catch (err) {
      logger.error('Failed to load summary');
    }
  }, [filterDays]);

  useEffect(() => {
    fetchHistory();
    fetchSummary();
  }, [fetchHistory, fetchSummary]);

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const changeTypeConfig = {
    delivery: { icon: Truck, color: 'text-red-700 bg-red-50', label: 'Delivery', prefix: '-' },
    revert: { icon: Undo2, color: 'text-emerald-700 bg-emerald-50', label: 'Revert', prefix: '+' },
    manual_add: { icon: ArrowUp, color: 'text-emerald-700 bg-emerald-50', label: 'Stock Added', prefix: '+' },
    manual_deduct: { icon: ArrowDown, color: 'text-red-700 bg-red-50', label: 'Stock Deducted', prefix: '-' },
    initial: { icon: Package, color: 'text-sky-700 bg-sky-50', label: 'Initial Stock', prefix: '+' }
  };

  const formatDate = (dateStr) => formatIST(dateStr);

  return (
    <div className="pb-6" data-testid="stock-history-page">
      {/* Header */}
      <div className="heda-card mb-4">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-heda-olive-tint flex items-center justify-center">
                <History className="w-5 h-5 text-heda-olive-dark" />
              </div>
              <div>
                <h1 className="font-display text-[20px] font-bold text-heda-ink">Stock History</h1>
                <p className="text-xs text-heda-mute">View all stock movements and changes</p>
              </div>
            </div>
            <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={() => navigate(-1)}>
              Back to Stock
            </Button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-red-50 rounded-xl p-3 border border-red-200">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-4 h-4 text-red-700" />
                <span className="text-xs text-red-700 font-medium">Deliveries</span>
              </div>
              <p className="text-lg font-bold text-red-700">
                -{Math.abs(summary.delivery?.total_qty || 0)}
              </p>
              <p className="text-[10px] text-red-700">{summary.delivery?.count || 0} transactions</p>
            </div>
            
            <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUp className="w-4 h-4 text-emerald-700" />
                <span className="text-xs text-emerald-700 font-medium">Stock Added</span>
              </div>
              <p className="text-lg font-bold text-emerald-700">
                +{Math.abs(summary.manual_add?.total_qty || 0) + Math.abs(summary.initial?.total_qty || 0)}
              </p>
              <p className="text-[10px] text-emerald-700">
                {(summary.manual_add?.count || 0) + (summary.initial?.count || 0)} transactions
              </p>
            </div>
            
            <div className="bg-sky-50 rounded-xl p-3 border border-sky-200">
              <div className="flex items-center gap-2 mb-1">
                <Undo2 className="w-4 h-4 text-sky-700" />
                <span className="text-xs text-sky-700 font-medium">Reverts</span>
              </div>
              <p className="text-lg font-bold text-sky-700">
                +{Math.abs(summary.revert?.total_qty || 0)}
              </p>
              <p className="text-[10px] text-sky-700">{summary.revert?.count || 0} transactions</p>
            </div>
            
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDown className="w-4 h-4 text-amber-700" />
                <span className="text-xs text-amber-700 font-medium">Manual Deduct</span>
              </div>
              <p className="text-lg font-bold text-amber-700">
                -{Math.abs(summary.manual_deduct?.total_qty || 0)}
              </p>
              <p className="text-[10px] text-amber-700">{summary.manual_deduct?.count || 0} transactions</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute" />
              <Input
                placeholder="Search by brand..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-xl pl-9 bg-heda-cream/50 border-heda-line text-sm"
              />
            </div>
            
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="rounded-xl w-[140px] bg-heda-cream/50 border-heda-line text-sm">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {STOCK_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="rounded-xl w-[140px] bg-heda-cream/50 border-heda-line text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
                <SelectItem value="revert">Revert</SelectItem>
                <SelectItem value="manual_add">Stock Added</SelectItem>
                <SelectItem value="manual_deduct">Stock Deducted</SelectItem>
                <SelectItem value="initial">Initial</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterDays} onValueChange={setFilterDays}>
              <SelectTrigger className="rounded-xl w-[120px] bg-heda-cream/50 border-heda-line text-sm">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Pre-filtered product context banner */}
      {(searchQuery || filterThickness || filterSize) && (
        <div>
          <div className="bg-heda-olive-tint border border-heda-olive/30 rounded-xl p-3 flex items-center justify-between" data-testid="product-filter-banner">
            <div className="flex-1">
              <p className="text-xs text-heda-sub mb-1">Filtered to product</p>
              <p className="text-sm font-semibold text-heda-olive-dark">
                {[searchQuery, filterThickness, filterSize].filter(Boolean).join(' • ')}
                {filterCategory !== 'all' && <span className="ml-2 text-xs font-normal">({filterCategory})</span>}
              </p>
              {(() => {
                const delivered = history.filter(h => h.change_type === 'delivery').reduce((s, h) => s + Math.abs(h.change_qty), 0);
                const added = history.filter(h => ['manual_add', 'initial'].includes(h.change_type)).reduce((s, h) => s + Math.abs(h.change_qty), 0);
                const customers = new Set(history.filter(h => h.customer_name).map(h => h.customer_name));
                return (
                  <p className="text-xs text-heda-sub mt-2">
                    <span className="inline-block mr-4"><strong className="text-red-700">-{delivered}</strong> delivered</span>
                    <span className="inline-block mr-4"><strong className="text-emerald-700">+{added}</strong> added</span>
                    <span className="inline-block"><strong>{customers.size}</strong> customer{customers.size === 1 ? '' : 's'}</span>
                  </p>
                );
              })()}
            </div>
            <Button className="rounded-xl"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setFilterThickness('');
                setFilterSize('');
                setFilterCategory('all');
                setSearchParams({});
              }}
              data-testid="clear-product-filter-btn"
            >
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* History List */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-heda-gold border-t-transparent rounded-full" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-heda-mute mx-auto mb-3" />
            <p className="text-heda-sub">No history found</p>
            <p className="text-sm text-heda-mute">Stock changes will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map(item => {
              const config = changeTypeConfig[item.change_type] || changeTypeConfig.manual_add;
              const Icon = config.icon;
              const isExpanded = expandedItems[item.id];

              return (
                <div 
                  key={item.id} 
                  className="bg-white rounded-xl border border-heda-line overflow-hidden"
                  data-testid={`history-item-${item.id}`}
                >
                  <button
                    onClick={() => toggleExpand(item.id)}
                    className="w-full p-3 flex items-center gap-3 hover:bg-heda-cream transition-colors"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    
                    <div className="flex-1 text-left">
                      <p className="font-medium text-sm text-heda-ink">
                        {item.brand_name} - {item.thickness} - {item.size}
                      </p>
                      <p className="text-xs text-heda-mute">
                        {item.category} • {formatDate(item.created_at)}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className={`font-mono font-bold text-lg ${item.change_qty > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {item.change_qty > 0 ? '+' : ''}{item.change_qty}
                      </p>
                      <p className="text-[10px] text-heda-mute">
                        {item.previous_stock} → {item.new_stock}
                      </p>
                    </div>

                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-heda-mute" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-heda-mute" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 pt-1 border-t border-heda-line bg-heda-cream">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-heda-mute">Type:</span>
                          <Badge className={`rounded-full ml-2 ${config.color}`}>{config.label}</Badge>
                        </div>
                        <div>
                          <span className="text-heda-mute">By:</span>
                          <span className="ml-2 font-medium">{item.user_name}</span>
                        </div>
                        {item.customer_name && (
                          <div className="col-span-2">
                            <span className="text-heda-mute">Customer:</span>
                            <span className="ml-2 font-medium">{item.customer_name}</span>
                          </div>
                        )}
                        {item.reference_number && (
                          <div className="col-span-2">
                            <span className="text-heda-mute">Reference:</span>
                            <span className="ml-2 font-mono text-heda-olive-dark">{item.reference_number}</span>
                          </div>
                        )}
                        {item.notes && (
                          <div className="col-span-2">
                            <span className="text-heda-mute">Notes:</span>
                            <span className="ml-2">{item.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
